import { useState, useEffect, useCallback } from 'react';
import { X, Search, Loader2, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import { useDebounce } from '../../hooks/useDebounce';
import {
  searchUsers,
  createInvitation,
  fetchInvitations,
  cancelInvitation,
  resendInvitation,
  type SearchUserResult,
  type InvitationItem,
} from '../../services/invitationService';
import Avatar from '../common/Avatar';
import type { ProfileColorKey } from '@maios/shared';

interface MemberInviteModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  onInvited: () => void;
}

function getDaysUntilExpiry(expiresAt: string): number {
  const diff = new Date(expiresAt).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
}

export default function MemberInviteModal({
  isOpen,
  onClose,
  projectId,
  onInvited,
}: MemberInviteModalProps) {
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchUserResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [pendingInvitations, setPendingInvitations] = useState<InvitationItem[]>([]);
  const [invitingEmail, setInvitingEmail] = useState<string | null>(null);
  const [resendingId, setResendingId] = useState<string | null>(null);
  const [cancellingId, setCancellingId] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('MEMBER');

  const debouncedQuery = useDebounce(searchQuery, 300);

  const fetchPendingInvitations = useCallback(async () => {
    try {
      const invitations = await fetchInvitations(projectId);
      setPendingInvitations(
        invitations.filter((inv) => inv.status === 'PENDING' || inv.status === 'EXPIRED'),
      );
    } catch {
      // silent
    }
  }, [projectId]);

  // 모달 열릴 때 초기화
  useEffect(() => {
    if (isOpen) {
      fetchPendingInvitations();
      setSearchQuery('');
      setSearchResults([]);
      setSelectedRole('MEMBER');
    }
  }, [isOpen, fetchPendingInvitations]);

  // 디바운스 검색
  useEffect(() => {
    if (!isOpen) return;
    if (!debouncedQuery || debouncedQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    let cancelled = false;
    setIsSearching(true);
    searchUsers(debouncedQuery, projectId)
      .then((results) => {
        if (!cancelled) setSearchResults(results);
      })
      .catch(() => {
        if (!cancelled) setSearchResults([]);
      })
      .finally(() => {
        if (!cancelled) setIsSearching(false);
      });

    return () => {
      cancelled = true;
    };
  }, [debouncedQuery, projectId, isOpen]);

  const handleInvite = async (email: string) => {
    setInvitingEmail(email);
    try {
      const result = await createInvitation(projectId, email, selectedRole);
      if (result.directAdded) {
        toast.success('멤버로 추가되었습니다.');
      } else if (!result.emailSent) {
        toast.success('초대가 생성되었습니다. (메일 발송 건너뜀)');
      } else {
        toast.success('초대 메일이 발송되었습니다.');
      }
      setSearchResults((prev) => prev.filter((u) => u.email !== email));
      fetchPendingInvitations();
      onInvited();
    } catch (error: unknown) {
      const msg =
        error && typeof error === 'object' && 'response' in error
          ? (error as { response?: { data?: { error?: string } } }).response?.data?.error
          : undefined;
      toast.error(msg || '초대에 실패했습니다.');
    } finally {
      setInvitingEmail(null);
    }
  };

  const handleCancel = async (invitationId: string) => {
    setCancellingId(invitationId);
    try {
      await cancelInvitation(projectId, invitationId);
      toast.success('초대가 취소되었습니다.');
      fetchPendingInvitations();
    } catch {
      toast.error('초대 취소에 실패했습니다.');
    } finally {
      setCancellingId(null);
    }
  };

  const handleResend = async (invitationId: string) => {
    setResendingId(invitationId);
    try {
      await resendInvitation(projectId, invitationId);
      toast.success('초대 메일이 재발송되었습니다.');
      fetchPendingInvitations();
    } catch {
      toast.error('재발송에 실패했습니다.');
    } finally {
      setResendingId(null);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={onClose} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="bg-white rounded-xl shadow-xl w-[520px] max-h-[80vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* 헤더 */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">멤버 초대</h2>
            <button
              onClick={onClose}
              className="p-1 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={18} />
            </button>
          </div>

          {/* 검색 */}
          <div className="px-6 py-3 border-b border-gray-100">
            <div className="relative">
              <Search
                size={16}
                className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"
              />
              <input
                type="text"
                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
                placeholder="이름 또는 이메일로 검색..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                autoFocus
              />
            </div>
            {searchQuery.length > 0 && searchQuery.length < 2 && (
              <p className="text-sm text-gray-400 mt-1.5">2자 이상 입력해주세요</p>
            )}
          </div>

          {/* CHG-043 REQ-V43-005: 역할 선택 */}
          <div className="px-6 py-3 border-b border-gray-100 flex items-center gap-3">
            <label className="text-sm text-gray-600 shrink-0">역할:</label>
            <select
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-blue-400"
            >
              <option value="PROJECT_MANAGER">프로젝트 매니저</option>
              <option value="PROJECT_LEADER">프로젝트 리더</option>
              <option value="MEMBER">멤버</option>
              <option value="STAKEHOLDER">이해관계자</option>
            </select>
          </div>

          {/* 검색 결과 + 초대 대기 목록 */}
          <div className="flex-1 overflow-y-auto">
            {/* 검색 결과 */}
            {isSearching && (
              <div className="flex justify-center py-6">
                <Loader2 className="size-5 animate-spin text-gray-400" />
              </div>
            )}
            {!isSearching &&
              debouncedQuery.length >= 2 &&
              searchResults.length === 0 && (
                <p className="text-sm text-gray-400 text-center py-6">
                  검색 결과가 없습니다
                </p>
              )}
            {searchResults.length > 0 && (
              <div className="px-4 py-2">
                {searchResults.map((user) => (
                  <div
                    key={user.userId}
                    className="flex items-center justify-between py-2.5 px-2 hover:bg-gray-50 rounded-lg"
                  >
                    <div className="flex items-center gap-3 min-w-0">
                      <Avatar
                        size="sm"
                        userId={user.userId}
                        name={user.name}
                        avatarUrl={user.profileImage}
                        displayColor={user.displayColor as ProfileColorKey | null | undefined}
                        firstName={user.firstName}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-gray-900 truncate">
                          {user.name}
                        </p>
                        <p className="text-sm text-gray-500 truncate">{user.email}</p>
                      </div>
                    </div>
                    <button
                      className="text-sm text-blue-600 border border-blue-300 rounded px-3 py-1 hover:bg-blue-50 transition-colors shrink-0 disabled:opacity-50"
                      onClick={() => handleInvite(user.email)}
                      disabled={invitingEmail === user.email}
                    >
                      {invitingEmail === user.email ? (
                        <Loader2 className="size-3.5 animate-spin" />
                      ) : (
                        '초대'
                      )}
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* 초대 대기 목록 */}
            {pendingInvitations.length > 0 && (
              <div className="px-4 py-3 border-t border-gray-100">
                <h3 className="text-sm font-semibold text-gray-500 uppercase tracking-wide mb-2 px-2">
                  초대 대기 중
                </h3>
                <div className="space-y-0.5">
                  {pendingInvitations.map((inv) => (
                    <div
                      key={inv.invitationId}
                      className="flex items-center justify-between py-2.5 px-2 rounded-lg"
                    >
                      <div className="min-w-0">
                        <p
                          className={`text-sm truncate ${inv.status === 'EXPIRED' ? 'text-gray-400' : 'text-gray-700'}`}
                        >
                          {inv.inviteeEmail}
                        </p>
                        <p className="text-sm text-gray-400">
                          {inv.inviter.name} 초대
                          {inv.status === 'EXPIRED'
                            ? ' · 만료됨'
                            : ` · ${getDaysUntilExpiry(inv.expiresAt)}일 남음`}
                        </p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        {inv.status === 'PENDING' && (
                          <>
                            <button
                              className="text-sm text-blue-500 hover:text-blue-700 hover:underline disabled:opacity-50"
                              onClick={() => handleResend(inv.invitationId)}
                              disabled={resendingId === inv.invitationId}
                            >
                              {resendingId === inv.invitationId ? (
                                <RefreshCw className="size-3 animate-spin" />
                              ) : (
                                '재발송'
                              )}
                            </button>
                            <button
                              className="text-sm text-red-500 hover:text-red-700 hover:underline disabled:opacity-50"
                              onClick={() => handleCancel(inv.invitationId)}
                              disabled={cancellingId === inv.invitationId}
                            >
                              취소
                            </button>
                          </>
                        )}
                        {inv.status === 'EXPIRED' && (
                          <span className="text-sm text-gray-400 bg-gray-100 px-2 py-0.5 rounded">
                            만료
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
